/*
 * <cart-drawer> — slide-in cart. Keeps Dawn's public API used by product forms and
 * <quick-atc-button>: open(trigger), close(), renderContents(parsedState), getSectionsToRender(),
 * setActiveElement(el). The drawer markup comes from sections/cart-drawer.liquid and is swapped
 * by VesperCart.render (assets/cart.js).
 * Requires cart.js to be loaded first.
 */
if (!customElements.get('cart-drawer')) {
  (() => {
    const OPEN_EVENT = 'vesper:cart-drawer:opened';

    class CartDrawer extends HTMLElement {
      connectedCallback() {
        this.addEventListener('keyup', (event) => {
          if (event.code === 'Escape' || event.key === 'Escape') this.close();
        });
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-cart-close]')) {
            event.preventDefault();
            this.close();
          }
        });
        if (this.hasAttribute('data-keep-open')) this.classList.add('active');
      }

      get panel() {
        return this.querySelector('.cart-drawer__panel');
      }

      open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        const wasOpen = this.classList.contains('active') && this.classList.contains('is-open');
        requestAnimationFrame(() => {
          this.classList.add('active', 'is-open');
          document.body.classList.add('overflow-hidden');
          if (!wasOpen) document.dispatchEvent(new CustomEvent(OPEN_EVENT));
        });
        const focusFirst = () => {
          const panel = this.panel;
          if (!panel || typeof trapFocus !== 'function') return;
          const target = panel.querySelector('.cart-drawer__close') || panel;
          trapFocus(panel, target);
        };
        if (wasOpen) focusFirst();
        else setTimeout(focusFirst, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 380);
      }

      close() {
        if (this.hasAttribute('data-keep-open')) return;
        this.classList.remove('active', 'is-open');
        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      }

      renderContents(parsedState) {
        this.classList.remove('is-empty');
        this.productId = parsedState && parsedState.id;
        // Other cart regions on the page refresh through the cartUpdate event the caller publishes.
        if (parsedState && parsedState.sections) window.VesperCart.render(parsedState.sections);
        this.open();
      }

      getSectionsToRender() {
        return [
          { id: 'cart-drawer', selector: '#CartDrawer' },
          { id: 'cart-icon-bubble' },
        ];
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }

      getSectionDOM(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      }

      setActiveElement(element) {
        this.activeElement = element;
      }
    }
    customElements.define('cart-drawer', CartDrawer);

    const BaseItems = customElements.get('cart-items') || window.CartItems;
    if (BaseItems && !customElements.get('cart-drawer-items')) {
      customElements.define('cart-drawer-items', class CartDrawerItems extends BaseItems {});
    }

    /* Header cart icon (and any [data-open-cart-drawer]) opens the drawer. */
    const drawer = () => document.querySelector('cart-drawer');
    const prepareIcon = () => {
      const link = document.getElementById('cart-icon-bubble');
      if (!link || !drawer()) return;
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
    };
    prepareIcon();
    document.addEventListener('shopify:section:load', prepareIcon);

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('#cart-icon-bubble, [data-open-cart-drawer]');
      const cart = drawer();
      if (!trigger || !cart) return;
      event.preventDefault();
      cart.open(trigger);
    });
    document.addEventListener('keydown', (event) => {
      if (event.code !== 'Space') return;
      const trigger = event.target.closest && event.target.closest('#cart-icon-bubble');
      const cart = drawer();
      if (!trigger || !cart) return;
      event.preventDefault();
      cart.open(trigger);
    });

    /* Theme editor: open the drawer while its section or blocks are selected. */
    if (window.Shopify && window.Shopify.designMode) {
      const isDrawer = (event) => event.target && event.target.closest && (event.target.closest('cart-drawer') || event.target.querySelector?.('cart-drawer'));
      document.addEventListener('shopify:section:select', (event) => isDrawer(event) && drawer()?.open());
      document.addEventListener('shopify:block:select', (event) => {
        if (!isDrawer(event)) return;
        drawer()?.open();
        setTimeout(() => event.target.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 400);
      });
      document.addEventListener('shopify:section:deselect', (event) => isDrawer(event) && drawer()?.close());
    }
  })();
}
